/**
 * 017-style structure, matching `../orders/filters.tsx` and `../customers/filters.tsx`: server-side
 * filters, each control writing the route's own search params so the loader re-fetches. Only the
 * four params `AuditController.QueryAudit` actually accepts are here — there is no `actor` filter
 * because the endpoint takes no `actorAccountId`, only `subjectAccountId` (who the event was about).
 *
 * `subjectAccountId` has no picker (Identity exposes no account-search endpoint this app calls) —
 * shown as a clear-only chip, same as `orders/filters.tsx`'s `customerId`: reached by a link from
 * elsewhere (a future "Audit trail" link off an account/customer screen — see routes.tsx), not typed
 * in here.
 */
import { useNavigate } from "react-router-dom";
import { PiXBold } from "react-icons/pi";
import { Button } from "rizzui";
import { StatusField } from "@steelyard/theme";
import { FILTER_LABEL } from "../../components/table/toolbar";
import { DateField } from "../../components/date-field";
import { AUDIT_EVENT_TYPES, eventTypeLabel, type AuditLogFilters } from "../../lib/services/audit";
import { parseIsoDate, toIsoDate } from "../../lib/format";

const EVENT_TYPE_OPTIONS = AUDIT_EVENT_TYPES.map((value) => ({ value, label: eventTypeLabel(value) }));


export function Filters({
  value,
  subjectName,
  query,
}: {
  value: AuditLogFilters;
  /** The subject account's server-sent name; never the id. */
  subjectName?: string | null;
  query: (over: Record<string, string | undefined>) => string;
}) {
  const navigate = useNavigate();
  const { eventType, from, to, subjectAccountId } = value;

  // A filter change means a different result set, so it resets the page — same rule every other
  // filters.tsx in this app follows.
  const apply = (over: Record<string, string | undefined>) => navigate(query({ page: undefined, ...over }));

  const fromDate = parseIsoDate(from ?? null);
  const toDate = parseIsoDate(to ?? null);

  return (
    <>
      <StatusField
        options={EVENT_TYPE_OPTIONS}
        value={EVENT_TYPE_OPTIONS.find((option) => option.value === eventType) ?? null}
        onChange={(option: { value: string } | null) => apply({ eventType: option?.value })}
        clearable
        onClear={() => apply({ eventType: undefined })}
        placeholder="Any event type"
        label="Event type"
        labelClassName={FILTER_LABEL}
        dropdownClassName="!z-10 h-auto"
        className="w-full @5xl:w-60"
      />
      {/* Two independent pickers, not one range field — see orders/filters.tsx for why:
          `suffix: null` drops the dropdown-caret icon (the calendar prefix is the only "open"
          affordance), and clearing is rizzui's own `clearable`/`onClear`, not react-datepicker's
          `isClearable` (a hard-coded blue circle that doesn't match this app's inputs). */}
      <DateField
        dateFormat="dd-MMM-yyyy"
        className="w-full @5xl:w-48"
        placeholderText="Occurred from…"
        selected={fromDate}
        maxDate={toDate ?? undefined}
        onChange={(date) => apply({ from: date ? toIsoDate(date as Date) : undefined })}
        inputProps={{
          label: "Occurred from",
          labelClassName: FILTER_LABEL,
          suffix: null,
          clearable: Boolean(fromDate),
          onClear: () => apply({ from: undefined }),
        }}
      />
      <DateField
        dateFormat="dd-MMM-yyyy"
        className="w-full @5xl:w-48"
        placeholderText="Occurred to…"
        selected={toDate}
        minDate={fromDate ?? undefined}
        onChange={(date) => apply({ to: date ? toIsoDate(date as Date) : undefined })}
        inputProps={{
          label: "Occurred to",
          labelClassName: FILTER_LABEL,
          suffix: null,
          clearable: Boolean(toDate),
          onClear: () => apply({ to: undefined }),
        }}
      />
      {subjectAccountId ? (
        <Button
          size="sm"
          variant="flat"
          className="h-9"
          aria-label={`Subject account ${subjectName ?? "Unknown user"}, clear this filter`}
          onClick={() => apply({ subjectAccountId: undefined })}
        >
          Subject <span className="ms-1">{subjectName ?? "Unknown user"}</span>
          <PiXBold aria-hidden className="ms-2 size-3.5" />
        </Button>
      ) : null}
    </>
  );
}
